import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, inr, fmtDate } from "@/components/common";
import DataTable from "@/components/DataTable";
import AdvancedFilters from "@/components/AdvancedFilters";
import QuickFilters from "@/components/QuickFilters";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { usePaged, useDebounced } from "@/lib/paged";
import { selectColumn, useIsAdmin, AdminDeleteBar } from "@/components/BulkEdit";

const QF = [{ key: "counselor_name", label: "Counselors" }, { key: "location", label: "Locations" }, { key: "requirement", label: "Requirements" }, { key: "next_action", label: "Next Actions" }, { key: "course", label: "Courses", get: (r) => (r.course || "").split(", ") }, { key: "colleges", label: "Colleges" }, { key: "last_disposition", label: "Last Dispositions" }];

export default function Counselling() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [quick, setQuick] = useState(null);
  const [display, setDisplay] = useState(null);
  const dq = useDebounced(search);
  const { rows: searched, complete, reload } = usePaged("/counselling", { search: dq.trim() });
  const isAdmin = useIsAdmin();
  const [sel, setSel] = useState([]);
  const [pageView, setPageView] = useState([]);
  const FIELDS = useMemo(() => [
    { key: "student_name", label: "Candidate", type: "text" }, { key: "requirement", label: "Requirement", type: "text" },
    { key: "budget", label: "Budget", type: "number" }, { key: "location", label: "Location", type: "text" },
    { key: "recommended_colleges", label: "Suggested Colleges", type: "text" }, { key: "visit_plan_date", label: "Visit Plan Date", type: "date" }, { key: "visited_date", label: "Visited Date", type: "date" }, { key: "next_action", label: "Next Action", type: "text" },
    { key: "counselor_name", label: "Counselor", type: "text" }, { key: "created_at", label: "Date", type: "date" },
  ], []);

  const columns = [
    selectColumn(pageView, sel, setSel, isAdmin),
    { key: "student_name", label: "Candidate", fixed: true, render: (r) => <span className="font-semibold text-slate-900" data-testid="counselling-candidate-name">{r.student_name || "—"}</span> },
    { key: "requirement", label: "Requirement", render: (r) => <span className="text-slate-700">{r.requirement || "—"}</span> },
    { key: "budget", label: "Budget", render: (r) => inr(r.budget) },
    { key: "location", label: "Location" },
    { key: "visit_plan_date", label: "Visit Plan", render: (r) => <span className="text-xs">{r.visit_plan_date ? fmtDate(r.visit_plan_date) : "—"}</span> },
    { key: "visited_date", label: "Visited", render: (r) => <span className="text-xs">{r.visited_date ? fmtDate(r.visited_date) : "—"}</span> },
    { key: "recommended_colleges", label: "Suggested Colleges", render: (r) => <span className="text-xs">{r.recommended_colleges || "—"}</span> },
    { key: "next_action", label: "Next Action", render: (r) => <span className="text-xs text-[#4F46E5]">{r.next_action || "—"}</span> },
    { key: "counselor_name", label: "Counselor" },
    { key: "created_at", label: "Date", render: (r) => <span className="text-xs">{fmtDate(r.created_at)}</span> },
    { key: "notes", label: "Notes", hidden: true, render: (r) => <span className="text-xs max-w-[240px] truncate inline-block">{r.notes || "—"}</span> },
  ];
  return (
    <div>
      <PageHeader title="Counselling" subtitle="Structured counselling sessions & recommendations" testid="counselling-header" />
      <div className="relative max-w-xs mb-3">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search all: candidate / mobile / requirement" className="pl-9 bg-white" data-testid="counselling-search" />
      </div>
      <QuickFilters rows={searched} fields={QF} onFiltered={setQuick} testid="counselling" />
      <AdvancedFilters entity="counselling" fields={FIELDS} rows={quick} onFiltered={setDisplay} />
      <AdminDeleteBar path="/counselling/bulk-delete" label="sessions" sel={sel} setSel={setSel} onDone={reload} note="The selected counselling sessions are removed. Leads and students are kept." />
      <DataTable columns={columns} rows={display} loading={searched === null} testid="counselling-table" columnsKey="counselling" paginate loadingMore={!complete} onPageRows={(r) => { setPageView(r); setSel([]); }} onRowClick={(r) => navigate(`/students/${r.student_id}`)} empty="No counselling sessions" />
    </div>
  );
}
